import React, { VoidFunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { DefaultPageLayout } from 'record-it-components/pageLayout/DefaultPageLayout/DefaultPageLayout';
import { NavigationItem } from 'record-it-components/pageLayout/NavigationSidebar/NavigationSidebar';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { IconUtils } from '../../../classes/icon/IconUtils';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useAuth } from '../../../hooks/useAuth';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { UserProfileDialog } from '../../user/UserProfileDialog/UserProfileDialog';

export const DefaultLayout: VoidFunctionComponent = React.memo((props) => {
  prepareProps({ props, ignoreChildren: true });

  const { t } = useTranslation();
  const { logout } = useAuth();

  const { getRequiredCurrentUserPermissions } = useCurrentUser();
  const adminWorkTimeData = useAdminWorkTimeData();

  const {
    open: openUserProfileDialog,
    close: closeUserProfileDialog,
    modalProps: userProfileDialogProps
  } = useModalOpened({ closeOnBackDropClick: true });

  const navigationItems = useMemo<Array<NavigationItem>>(() => {
    const currentUserPermissions = getRequiredCurrentUserPermissions();
    const firstCompilationUserGroup =
      currentUserPermissions.getEditableUserGroupsForTimeTrackingActivityCompilations()[0];

    return getAccessibleNavigationItems([
      {
        ...IconUtils.thinner(
          iconConfigurations.prefixed.page.HomePage.titleIcon
        ),
        label: t('components.layout.DefaultLayout.navigationItems.home'),
        to: RoutePaths.home(),
        isAccessible: true
      },
      {
        ...IconUtils.thinner(
          iconConfigurations.prefixed.page.MyWorkTimeOverviewPage.titleIcon
        ),
        label: t(
          'components.layout.DefaultLayout.navigationItems.myWorkTimeOverviewPage'
        ),
        to: RoutePaths.myWorkTimeOverview(),
        isAccessible: true
      },
      ...(adminWorkTimeData
        ? [
            {
              ...IconUtils.thinner(
                iconConfigurations.prefixed.page.MyWorkTimeOverviewPage
                  .titleIcon
              ),
              label: t(
                'components.layout.DefaultLayout.navigationItems.adminWorkTime'
              ),
              to: RoutePaths.adminWorkTime(
                adminWorkTimeData.userGroupId,
                adminWorkTimeData.userId
              ),
              isAccessible: true
            }
          ]
        : []),
      {
        ...IconUtils.thinner(
          iconConfigurations.prefixed.page.TimeTrackingConfigurationAdminPage
            .titleIcon
        ),
        label: t(
          'components.layout.DefaultLayout.navigationItems.timeTrackingConfiguration'
        ),
        to: RoutePaths.adminTimeTrackingConfigurations(),
        isAccessible: currentUserPermissions.canEditTimeTrackingConfigurations()
      },
      {
        ...IconUtils.thinner(
          iconConfigurations.prefixed.page.ConstructionSiteAdminPage.titleIcon
        ),
        label: t(
          'components.layout.DefaultLayout.navigationItems.constructionSite'
        ),
        to: RoutePaths.adminConstructionSites(),
        isAccessible: currentUserPermissions.canEditConstructionSites()
      },
      ...(firstCompilationUserGroup
        ? [
            {
              ...IconUtils.thinner(
                iconConfigurations.prefixed.page
                  .TimeTrackingActivityCompilationAdminPage.titleIcon
              ),
              label: t(
                'components.layout.DefaultLayout.navigationItems.timeTrackingActivityCompilation'
              ),
              to: RoutePaths.adminTimeTrackingActivityCompilations(
                firstCompilationUserGroup.id
              ),
              isAccessible: true
            }
          ]
        : []),
      {
        ...IconUtils.thinner(
          iconConfigurations.prefixed.page.UserAdminPage.titleIcon
        ),
        label: t('components.layout.DefaultLayout.navigationItems.users'),
        to: RoutePaths.adminUsers(),
        isAccessible: currentUserPermissions.canEditUsers()
      },
      {
        ...IconUtils.thinner(
          iconConfigurations.prefixed.page.UserGroupAdminPage.titleIcon
        ),
        label: t('components.layout.DefaultLayout.navigationItems.userGroups'),
        to: RoutePaths.adminUserGroups(),
        isAccessible: currentUserPermissions.canEditUserGroups()
      }
    ]);
  }, [t, getRequiredCurrentUserPermissions, adminWorkTimeData]);

  return (
    <DefaultPageLayout
      navigationItems={navigationItems}
      onLogoutClick={logout}
      onOpenUserProfile={openUserProfileDialog}
    >
      <Outlet></Outlet>
      <UserProfileDialog
        {...userProfileDialogProps}
        onAccept={closeUserProfileDialog}
      ></UserProfileDialog>
    </DefaultPageLayout>
  );
});

function useAdminWorkTimeData(): AdminWorkTimeData | null {
  const { getRequiredCurrentUserPermissions } = useCurrentUser();
  const userGroups =
    getRequiredCurrentUserPermissions().getEditableUserGroupsForWorkTimeEntries();

  const userGroupToUserRepository = useEntityRepository(
    EntityName.UserGroupToUser
  );

  for (const userGroup of userGroups) {
    const firstRelation = userGroupToUserRepository.getByUserGroupId(
      userGroup.id
    )[0];
    if (firstRelation) {
      return {
        userGroupId: userGroup.id,
        userId: firstRelation.userId
      };
    }
  }

  return null;
}

function getAccessibleNavigationItems(
  items: Array<NavigationItem & { isAccessible: boolean }>
): Array<NavigationItem> {
  return items.filter((item) => item.isAccessible);
}

type AdminWorkTimeData = {
  userGroupId: string;
  userId: string;
};
